import { RimbuError } from '@rimbu/base';
import { TraverseState } from '@rimbu/common';
import { Stream } from '@rimbu/stream';
export class BiMultiMapBuilder {
    constructor(context, source) {
        this.context = context;
        this.source = source;
        this._lock = 0;
        // prettier-ignore
        this.hasKey = (key) => {
            return this.source?.hasKey(key) ?? this.keyValueMultiMap.hasKey(key);
        };
        // prettier-ignore
        this.hasValue = (value) => {
            return this.source?.hasValue(value) ?? this.valueKeyMultiMap.hasKey(value);
        };
        this.hasEntry = (key, value) => {
            return (this.source?.hasEntry(key, value) ??
                this.keyValueMultiMap.hasEntry(key, value));
        };
        // prettier-ignore
        this.getValues = (key) => {
            if (undefined !== this.source) {
                return this.source.getValues(key);
            }
            return this.keyValueMultiMap.getValues(key);
        };
        // prettier-ignore
        this.getKeys = (value) => {
            if (undefined !== this.source) {
                return this.source.getKeys(value);
            }
            return this.valueKeyMultiMap.getValues(value);
        };
        this.setValues = (key, values) => {
            this.checkLock();
            const removed = this.removeKey(key);
            const added = this.addEntries(Stream.from(values).map((value) => [key, value]));
            return removed || added;
        };
        this.setKeys = (value, keys) => {
            this.checkLock();
            const removed = this.removeValue(value);
            const added = this.addEntries(Stream.from(keys).map((key) => [key, value]));
            return removed || added;
        };
        this.add = (key, value) => {
            this.checkLock();
            if (!this.keyValueMultiMap.add(key, value))
                return false;
            this.source = undefined;
            return this.valueKeyMultiMap.add(value, key);
        };
        this.addEntries = (entries) => {
            this.checkLock();
            return Stream.applyFilter(entries, { pred: this.add }).count() > 0;
        };
        // prettier-ignore
        this.removeKey = (key) => {
            this.checkLock();
            const values = this.getValues(key);
            if (values.isEmpty)
                return false;
            this.keyValueMultiMap.removeKey(key);
            this.source = undefined;
            return this.valueKeyMultiMap.removeEntries(values.stream().map((value) => [value, key]));
        };
        // prettier-ignore
        this.removeKeys = (keys) => {
            this.checkLock();
            return Stream.from(keys).filterPure({ pred: this.removeKey }).count() > 0;
        };
        // prettier-ignore
        this.removeValue = (value) => {
            this.checkLock();
            const keys = this.getKeys(value);
            if (keys.isEmpty)
                return false;
            this.valueKeyMultiMap.removeKey(value);
            this.source = undefined;
            return this.keyValueMultiMap.removeEntries(keys.stream().map((key) => [key, value]));
        };
        // prettier-ignore
        this.removeValues = (values) => {
            this.checkLock();
            return Stream.from(values).filterPure({ pred: this.removeValue }).count() > 0;
        };
        this.removeEntry = (key, value) => {
            this.checkLock();
            if (!this.keyValueMultiMap.removeEntry(key, value))
                return false;
            this.source = undefined;
            return this.valueKeyMultiMap.removeEntry(value, key);
        };
        this.removeEntries = (entries) => {
            this.checkLock();
            return Stream.applyFilter(entries, { pred: this.removeEntry }).count() > 0;
        };
        this.forEach = (f, options = {}) => {
            const { state = TraverseState() } = options;
            this._lock++;
            this.keyValueMultiMap.forEach(f, { state });
            this._lock--;
        };
        this.build = () => {
            if (undefined !== this.source)
                return this.source;
            if (this.isEmpty) {
                return this.context.empty();
            }
            const keyValueMultiMap = this.keyValueMultiMap.build().assumeNonEmpty();
            const valueKeyMultiMap = this.valueKeyMultiMap.build().assumeNonEmpty();
            return this.context.createNonEmpty(keyValueMultiMap, valueKeyMultiMap);
        };
    }
    get keyValueMultiMap() {
        if (undefined === this._keyValueMultiMap) {
            if (undefined === this.source) {
                this._keyValueMultiMap = this.context.keyValueMultiMapContext.builder();
                this._valueKeyMultiMap = this.context.valueKeyMultiMapContext.builder();
            }
            else {
                this._keyValueMultiMap = this.source.keyValueMultiMap.toBuilder();
                this._valueKeyMultiMap = this.source.valueKeyMultiMap.toBuilder();
            }
        }
        return this._keyValueMultiMap;
    }
    get valueKeyMultiMap() {
        if (undefined === this._valueKeyMultiMap) {
            if (undefined === this.source) {
                this._keyValueMultiMap = this.context.keyValueMultiMapContext.builder();
                this._valueKeyMultiMap = this.context.valueKeyMultiMapContext.builder();
            }
            else {
                this._keyValueMultiMap = this.source.keyValueMultiMap.toBuilder();
                this._valueKeyMultiMap = this.source.valueKeyMultiMap.toBuilder();
            }
        }
        return this._valueKeyMultiMap;
    }
    checkLock() {
        if (this._lock)
            RimbuError.throwModifiedBuilderWhileLoopingOverItError();
    }
    get size() {
        return this.source?.size ?? this.keyValueMultiMap.size;
    }
    get isEmpty() {
        return this.size === 0;
    }
}
//# sourceMappingURL=builder.mjs.map